import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  AspectChip,
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  Person,
  SectionLabel,
} from '../../../design/components'
import {
  MEMBER_ROLE_ORDER,
  RELATIONSHIP_ORDER,
  memberRoleLabel,
  relationshipLabel,
  relationshipTone,
} from '../labels'
import type { CampaignCharacter, Faction, FactionsSection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

type Schema = components['schemas']

/**
 * The powers in the city. Section `factions`.
 *
 * Design spec §5.4 asks for the city's threats and the faces behind them; this is the
 * threats half, and it reuses the aspect chip for the one thing a faction says about
 * another — the relationship — exactly as the spec promises: no new components.
 *
 * Every write here is Game Master only, and the affordances are driven by the server's
 * `isGameMaster` rather than by guessing from the shape of the data.
 */
export function FactionsCard({
  factions,
  roster,
  editing,
}: {
  factions: FactionsSection | undefined
  /** The campaign roster — a faction member must already be in play. */
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
}) {
  const [adding, setAdding] = useState(false)

  if (factions === undefined) return null

  const all = factions.factions ?? []
  const canEdit = editing.access.isGameMaster

  return (
    <CollapsibleCard
      variant="harm"
      label="Factions"
      summary={all.length === 1 ? '1 faction' : `${all.length} factions`}
      defaultOpen={all.length > 0}
      action={
        canEdit && !adding ? (
          <IconButton label="Add a faction" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && <p className={styles.empty}>No factions in this city yet.</p>}

      {all.map((faction) => (
        <FactionRow
          key={faction.id}
          faction={faction}
          others={all.filter((candidate) => candidate.id !== faction.id)}
          roster={roster}
          editing={editing}
        />
      ))}

      {adding && <FactionForm editing={editing} onClose={() => setAdding(false)} />}
    </CollapsibleCard>
  )
}

function FactionRow({
  faction,
  others,
  roster,
  editing,
}: {
  faction: Faction
  others: readonly Faction[]
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
}) {
  const [open, setOpen] = useState<'none' | 'edit' | 'relationship' | 'member'>('none')
  const canEdit = editing.access.isGameMaster

  if (open === 'edit') {
    return <FactionForm faction={faction} editing={editing} onClose={() => setOpen('none')} />
  }

  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <span className={styles.rowName}>{faction.name}</span>
        {canEdit && (
          <IconButton label={`Edit ${faction.name}`} icon="edit" onClick={() => setOpen('edit')} />
        )}
      </div>

      {faction.goal !== '' && (
        <p className={styles.rowText}>
          <span className={styles.tag}>Goal</span> {faction.goal}
        </p>
      )}
      {faction.description !== '' && <p className={styles.rowText}>{faction.description}</p>}

      {faction.relationships.length > 0 && (
        <div className={styles.chips}>
          {faction.relationships.map((relationship) => {
            const text = `${relationshipLabel(String(relationship.type))} · ${relationship.relatedFactionName ?? 'Unknown'}`
            const variant = relationshipTone(String(relationship.type))

            // A chip with an onClick renders as a button; without one, as a span.
            // Only the GM gets the button, so a player's chips stay unfocusable
            // rather than being focusable controls that do nothing.
            return canEdit ? (
              <AspectChip
                key={relationship.id}
                text={text}
                variant={variant}
                disabled={editing.removeRelationship.isPending}
                onClick={() =>
                  editing.removeRelationship.mutate({ relationshipId: relationship.id })
                }
              />
            ) : (
              <AspectChip key={relationship.id} text={text} variant={variant} />
            )
          })}
        </div>
      )}

      {canEdit && faction.relationships.length > 0 && (
        <p className={styles.note}>
          {/* The store is one-directional and the API keeps it that way, so saying
              "allied" here does not make the other faction say it back. Worth stating:
              a reader would otherwise assume the edge is mutual. */}
          Relationships point one way. Tap one to remove it.
        </p>
      )}

      <div className={styles.subBlock}>
        <SectionLabel tone="muted" micro>
          Members
        </SectionLabel>

        {faction.members.length === 0 ? (
          <p className={styles.empty}>Nobody in this faction.</p>
        ) : (
          <div className={styles.subList}>
            {faction.members.map((member) => (
              <div key={member.id} className={styles.subRow}>
                <Person
                  name={member.characterName ?? 'Unknown character'}
                  portraitUrl={member.portraitUrl}
                  meta={memberRoleLabel(String(member.role))}
                  characterId={member.characterId}
                />
                {canEdit && (
                  <Button
                    variant="danger"
                    disabled={editing.removeMember.isPending}
                    onClick={() => editing.removeMember.mutate({ memberId: member.id })}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {canEdit && open === 'relationship' && (
        <RelationshipForm
          faction={faction}
          others={others}
          editing={editing}
          onClose={() => setOpen('none')}
        />
      )}

      {canEdit && open === 'member' && (
        <MemberForm
          faction={faction}
          roster={roster}
          editing={editing}
          onClose={() => setOpen('none')}
        />
      )}

      {canEdit && open === 'none' && (
        <div className={styles.rowActions}>
          <Button
            variant="secondary"
            disabled={others.length === 0}
            onClick={() => setOpen('relationship')}
          >
            Add relationship
          </Button>
          <Button
            variant="secondary"
            disabled={roster.length === 0}
            onClick={() => setOpen('member')}
          >
            Add member
          </Button>
          <Button
            variant="danger"
            disabled={editing.removeFaction.isPending}
            onClick={() => editing.removeFaction.mutate({ factionId: faction.id })}
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  )
}

function FactionForm({
  faction,
  editing,
  onClose,
}: {
  faction?: Faction | undefined
  editing: CampaignEditing
  onClose: () => void
}) {
  const [name, setName] = useState(faction?.name ?? '')
  const [goal, setGoal] = useState(faction?.goal ?? '')
  const [description, setDescription] = useState(faction?.description ?? '')

  const creating = faction === undefined
  const pending = editing.addFaction.isPending || editing.updateFaction.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        const body = {
          name,
          goal: goal === '' ? null : goal,
          description: description === '' ? null : description,
        }

        if (creating) {
          editing.addFaction.mutate(body, { onSuccess: onClose })
        } else {
          editing.updateFaction.mutate({ factionId: faction.id, body }, { onSuccess: onClose })
        }
      }}
    >
      <SectionLabel tone="ember" micro>
        {creating ? 'New faction' : `Edit ${faction.name}`}
      </SectionLabel>

      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Input label="Goal" value={goal} onChange={(e) => setGoal(e.target.value)} />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Description
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {pending ? 'Saving…' : creating ? 'Add' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

function RelationshipForm({
  faction,
  others,
  editing,
  onClose,
}: {
  faction: Faction
  others: readonly Faction[]
  editing: CampaignEditing
  onClose: () => void
}) {
  const [relatedFactionId, setRelatedFactionId] = useState(String(others[0]?.id ?? ''))
  const [type, setType] = useState<Schema['RelationshipType']>('Neutral')

  const pending = editing.addRelationship.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addRelationship.mutate(
          {
            factionId: faction.id,
            body: { relatedFactionId: Number(relatedFactionId), type },
          },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        {faction.name} regards…
      </SectionLabel>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Faction
        </SectionLabel>
        <select
          className={styles.select}
          value={relatedFactionId}
          onChange={(e) => setRelatedFactionId(e.target.value)}
        >
          {others.map((other) => (
            <option key={other.id} value={other.id}>
              {other.name}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          As
        </SectionLabel>
        <select
          className={styles.select}
          value={type}
          onChange={(e) => setType(e.target.value as Schema['RelationshipType'])}
        >
          {RELATIONSHIP_ORDER.map((option) => (
            <option key={option} value={option}>
              {relationshipLabel(option)}
            </option>
          ))}
        </select>
      </label>

      <p className={styles.note}>
        This points one way. Add the reverse separately if both should see it.
      </p>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || relatedFactionId === ''}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}

/**
 * Members come from the campaign roster, not from the whole character table — the API
 * rejects anyone not in play here, because factions describe the people at this table.
 */
function MemberForm({
  faction,
  roster,
  editing,
  onClose,
}: {
  faction: Faction
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
  onClose: () => void
}) {
  const alreadyIn = new Set(faction.members.map((member) => member.characterId))
  const candidates = roster.filter((character) => !alreadyIn.has(character.characterId))

  const [characterId, setCharacterId] = useState(String(candidates[0]?.characterId ?? ''))
  const [role, setRole] = useState<Schema['MemberRole']>('Member')

  const pending = editing.addMember.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addMember.mutate(
          { factionId: faction.id, body: { characterId: Number(characterId), role } },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        Add to {faction.name}
      </SectionLabel>

      {candidates.length === 0 ? (
        <p className={styles.note}>Everyone on the roster is already in this faction.</p>
      ) : (
        <>
          <label className={styles.field}>
            <SectionLabel tone="muted" micro>
              Character
            </SectionLabel>
            <select
              className={styles.select}
              value={characterId}
              onChange={(e) => setCharacterId(e.target.value)}
            >
              {candidates.map((character) => (
                <option key={character.characterId} value={character.characterId}>
                  {character.name}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <SectionLabel tone="muted" micro>
              Role
            </SectionLabel>
            <select
              className={styles.select}
              value={role}
              onChange={(e) => setRole(e.target.value as Schema['MemberRole'])}
            >
              {MEMBER_ROLE_ORDER.map((option) => (
                <option key={option} value={option}>
                  {memberRoleLabel(option)}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || characterId === '' || candidates.length === 0}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
